import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import clsx from 'clsx'
import { connect } from 'react-redux'
import LayoutContainer from 'components/Common/LayoutContainer'
import {
  arrayToObject,
  EmptyArray,
  groupBy,
  sortBy,
  sum,
} from 'lib/array/arrayUtils'
import {
  AnythingCategory,
  EXP_CATEGORY_DAY_TRIPS_ID,
  EXP_CATEGORY_FOOD_AND_DRINK_ID,
  EXP_CATEGORY_GIFT_INSPIRATION,
  EXP_CATEGORY_GUIDED_TOURS_ID,
  EXP_CATEGORY_LUX_EXCLUSIVE_ID,
  EXP_CATEGORY_LUX_PLUS_ID,
  EXP_CATEGORY_SHOWS_AND_EVENTS_ID,
  EXP_CATEGORY_TICKETS_AND_PASSES_ID,
  EXP_CATEGORY_TOP_ACTIVITIES_ID,
  EXP_CATEGORY_WELLNESS_AND_SPA_ID,
} from 'constants/experience'
import useExperiencesSearchFilters from 'components/Experiences/hooks/useExperiencesSearchFilters'
import { sortCategoriesByRelevance } from 'lib/experiences/categoryRelevanceRanking'
import useMostPopulatedSearchExperienceFacets from '../hooks/useMostPopulatedSearchExperienceFacets'
import { type ExperienceLandmarksToggleState } from './ExperienceSearchLandmarksToggle'
import LineLandmarkIcon from 'LuxKit/Icons/line/LineLandmarkIcon'
import ToggleButton from 'LuxKit/ToggleButton'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import CheckboxInputListItem from 'LuxKit/List/CheckboxInputListItem'
import useToggle from 'hooks/useToggle'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import LineRestaurantIcon from 'LuxKit/Icons/line/LineRestaurantIcon'
import LineGuidedtoursIcon from 'LuxKit/Icons/line/LineGuidedtoursIcon'
import LineWellnessIcon from 'LuxKit/Icons/line/LineWellnessIcon'
import LineStarIcon from 'LuxKit/Icons/line/LineStarIcon'
import LineHotAirBalloonIcon from 'LuxKit/Icons/line/LineHotAirBalloonIcon'
import LineTicketIcon from 'LuxKit/Icons/line/LineTicketIcon'
import LineTheatreIcon from 'LuxKit/Icons/line/LineTheatreIcon'
import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import LineCarIcon from 'LuxKit/Icons/line/LineCarIcon'
import {
  EXP_CATEGORY_LOUNGES_AND_SERVICES_SEARCH_ID,
  EXP_CATEGORY_TRANSFERS_SEARCH_ID,
  getNextCategoryCodesForSearchCategory,
  splitTravelEssentialsFilterCategories,
} from './experienceSearchTravelEssentialsSplit'
import useExperiment from 'hooks/Optimizely/useExperiment'
import { Experiments } from 'constants/optimizely'
import useTrackExperienceSearchFilter from 'components/Experiences/hooks/useTrackExperienceSearchFilter'

const CATEGORY_ICON_MAP: Record<number, React.ReactElement> = {
  [EXP_CATEGORY_FOOD_AND_DRINK_ID]: <LineRestaurantIcon />,
  [EXP_CATEGORY_TOP_ACTIVITIES_ID]: <LineHotAirBalloonIcon />,
  [EXP_CATEGORY_TICKETS_AND_PASSES_ID]: <LineTicketIcon />,
  [EXP_CATEGORY_SHOWS_AND_EVENTS_ID]: <LineTheatreIcon />,
  [EXP_CATEGORY_WELLNESS_AND_SPA_ID]: <LineWellnessIcon />,
  [EXP_CATEGORY_DAY_TRIPS_ID]: <LinePlaneDepartureIcon />,
  [EXP_CATEGORY_GUIDED_TOURS_ID]: <LineGuidedtoursIcon />,
  [EXP_CATEGORY_TRANSFERS_SEARCH_ID]: <LineCarIcon />,
  [EXP_CATEGORY_LOUNGES_AND_SERVICES_SEARCH_ID]: <LinePlaneDepartureIcon />,
}

const DEFAULT_CATEGORY_ICON = <LineStarIcon />
const LANDMARKS_ICON = <LineLandmarkIcon />

const CategoryToggleButton = styled(ToggleButton)`
  &.kind-secondary {
    border-color: transparent;
    box-shadow: inset 0 0 0 1px
      ${(props) => props.theme.palette.neutral.default.six};

    &.is-selected {
      box-shadow: inset 0 0 0 1px
        ${(props) => props.theme.palette.neutral.default.one};
    }
  }
`

const FiltersContainer = styled.div`
  border-top: 1px solid ${(props) => props.theme.palette.neutral.default.five};
  padding-top: ${rem(12)};
  padding-bottom: ${rem(4)};
`

const FiltersRow = styled(LayoutContainer)<{ $hasOverflow?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${rem(12)};
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }

  > * {
    flex-shrink: 0;
  }
`

const FullWidthFiltersRow = styled(FiltersRow)`
  max-width: 100vw !important;
  padding-left: ${rem(16)} !important;
  padding-right: ${rem(16)} !important;
`

const ListViewFiltersRow = styled(FiltersRow)`
  justify-content: center;

  &.align-start {
    justify-content: flex-start;
  }

  ${(props) => props.$hasOverflow && `
    max-width: 100vw !important;
    padding-left: ${rem(16)} !important;
    padding-right: ${rem(16)} !important;
    justify-content: flex-start;
  `}
`

interface CategoryFilterItem {
  name: string;
  count: number;
  value: number;
  checked: boolean;
  hidden: boolean;
  disabled: boolean;
}

interface CategoryItem {
  category: App.ExperienceItemCategory;
  categoryId: number;
  count: number;
  items: Array<CategoryFilterItem>;
  name: string;
  title: string;
}

interface CategoryFilterButtonProps {
  categoryItem: CategoryItem;
  onApplyFilters: (
    categoryItem: CategoryItem,
    selectedIds: Set<number>,
  ) => void;
  onResetCategory: (item: CategoryItem) => void;
}

function CategoryFilterButton({
  categoryItem,
  onApplyFilters,
  onResetCategory,
}: CategoryFilterButtonProps) {
  const intl = useIntl()
  const trackSearchFilter = useTrackExperienceSearchFilter()
  const [isOpen, toggleOpen, , closeDropdown] = useToggle()
  const triggerRef = useRef<HTMLButtonElement>(null)
  const [pendingChecked, setPendingChecked] = useState<Set<number>>(
    () =>
      new Set(
        categoryItem.items
          .filter((filterItem) => filterItem.checked)
          .map((filterItem) => filterItem.value),
      ),
  )

  useEffect(() => {
    if (isOpen) {
      setPendingChecked(
        new Set(
          categoryItem.items
            .filter((filterItem) => filterItem.checked)
            .map((filterItem) => filterItem.value),
        ),
      )
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  const selectedCount = categoryItem.items.filter(
    (item) => item.checked,
  ).length
  const categoryName = categoryItem.title
  const icon =
    CATEGORY_ICON_MAP[categoryItem.category.id] ?? DEFAULT_CATEGORY_ICON

  const handleToggle = useCallback((value: number) => {
    setPendingChecked((prev) => {
      const next = new Set(prev)
      if (next.has(value)) {
        next.delete(value)
      } else {
        next.add(value)
      }
      return next
    })
  }, [])

  const onReset = useCallback(() => {
    if (selectedCount > 0) {
      trackSearchFilter({
        label: categoryItem.title,
        type: 'category',
        value: 'cleared',
      })
    }
    onResetCategory(categoryItem)
    closeDropdown()
  }, [categoryItem, selectedCount, trackSearchFilter, onResetCategory, closeDropdown])

  const onApply = useCallback(() => {
    const filterContexts = categoryItem.items
      .filter((item) => item.checked !== pendingChecked.has(item.value))
      .map((item) => ({
        label: item.name,
        type: 'category' as const,
        value: pendingChecked.has(item.value) ? 'selected' : 'unselected',
      }))
    trackSearchFilter(filterContexts)
    onApplyFilters(categoryItem, pendingChecked)
    closeDropdown()
  }, [categoryItem, pendingChecked, trackSearchFilter, onApplyFilters, closeDropdown])

  const visibleItems = useMemo(
    () => categoryItem.items.filter((item) => !item.hidden),
    [categoryItem.items],
  )
  const selectableItems = useMemo(
    () => visibleItems.filter((item) => !item.disabled),
    [visibleItems],
  )

  const allSelected = useMemo(
    () =>
      selectableItems.length > 0 &&
      selectableItems.every((item) => pendingChecked.has(item.value)),
    [selectableItems, pendingChecked],
  )

  const handleSelectAll = useCallback(() => {
    setPendingChecked((prev) => {
      const next = new Set(prev)
      if (selectableItems.every((item) => next.has(item.value))) {
        selectableItems.forEach((item) => next.delete(item.value))
      } else {
        selectableItems.forEach((item) => next.add(item.value))
      }
      return next
    })
  }, [selectableItems])

  return (
    <div>
      <CategoryToggleButton
        ref={triggerRef}
        kind="secondary"
        orientation="horizontal"
        isSelected={!!selectedCount}
        title={categoryName}
        startIcon={icon}
        endIcon={<LineAngleDownIcon />}
        onClick={toggleOpen}
      />
      <DropdownList
        size="S"
        width="max"
        triggerRef={triggerRef}
        anchorRef={triggerRef}
        open={isOpen}
        placement="bottom-start"
        onClose={closeDropdown}
        title={categoryName}
        hasDismissButton
        primaryActionProps={{
          children: intl.formatMessage({
            id: 'XGAxBfjicX0x40wpm7Xt',
            defaultMessage: 'Apply',
            description:
              'Button text to apply sort and filter selections in experience search',
          }),
          onClick: onApply,
          style: { flex: 1 },
        }}
        secondaryActionProps={{
          kind: 'tertiary',
          children: intl.formatMessage({
            id: '61b-YHMXB-JsuWKpgENc',
            defaultMessage: 'Reset',
            description:
              'Button text to reset filter selections in experience search map filter dropdown',
          }),
          onClick: onReset,
        }}
      >
        {visibleItems.length === 0 && (
          <BodyText variant="medium" style={{ paddingLeft: rem(16) }}>
            {intl.formatMessage({
              id: 'dagJf2WFiyWNfetEbv29',
              defaultMessage: 'No available categories',
              description:
                'Empty state message when no categories are available for filtering in experience search map',
            })}
          </BodyText>
        )}
        {visibleItems.length > 0 && (
          <CheckboxInputListItem
            inputPlacement="start"
            checked={allSelected}
            onChange={handleSelectAll}
            disabled={selectableItems.length === 0}
            title={intl.formatMessage({
              id: 'GC6-4lugK4uyh65fIwhd',
              defaultMessage: 'Select all',
              description:
                'Option to select all subcategories within a category filter',
            })}
            size="medium"
          />
        )}
        {visibleItems.map((item) => (
          <CheckboxInputListItem
            key={item.value}
            inputPlacement="start"
            checked={pendingChecked.has(item.value)}
            onChange={() => handleToggle(item.value)}
            title={item.name}
            size="medium"
            disabled={item.disabled}
          >
            <BodyText variant="medium" colour="neutral-two">
              {intl.formatMessage(
                {
                  id: 'AK3J75yI0gs1r7X6v_It',
                  defaultMessage: '({count})',
                  description:
                    'Count of matching experiences shown next to a category filter option',
                },
                { count: item.count },
              )}
            </BodyText>
          </CheckboxInputListItem>
        ))}
      </DropdownList>
    </div>
  )
}

interface MappedProps {
  categories: Array<App.ExperienceItemCategory>;
}

interface Props {
  place?: App.Place;
  isMapView?: boolean;
  landmarksToggle?: ExperienceLandmarksToggleState;
}

function ExperienceSearchCategoryFilters({
  place,
  isMapView,
  landmarksToggle,
  categories = EmptyArray,
}: Props & MappedProps) {
  const intl = useIntl()
  const { isAnyVariant: discoveryRevampEnabled } = useExperiment(
    Experiments.experiencesSearchDiscoveryRevamp,
  )
  const { searchFilters, setSearchFilters } = useExperiencesSearchFilters()
  const filtersRowRef = useRef<HTMLDivElement>(null)
  const [hasListViewOverflow, setHasListViewOverflow] = useState(false)

  const { categories: categoriesFacets, primaryCategories } =
    useMostPopulatedSearchExperienceFacets(place, {
      rankCategoriesByRelevance: discoveryRevampEnabled,
      prioritiseSelectedCategories: discoveryRevampEnabled,
      sendPerformanceVariant: discoveryRevampEnabled,
    })

  const onApplyFilters = useCallback(
    (categoryItem: CategoryItem, selectedIds: Set<number>) => {
      const currentCategories = searchFilters.categoryCodes ?? []
      setSearchFilters({
        ...searchFilters,
        categoryCodes: getNextCategoryCodesForSearchCategory(
          currentCategories,
          categoryItem.category.id,
          categoryItem.items.map((filterItem) => filterItem.value),
          Array.from(selectedIds),
        ),
      })
    },
    [searchFilters, setSearchFilters],
  )

  const EXCLUDED_CATEGORY_IDS = useMemo(
    () =>
      new Set([
        AnythingCategory.id,
        EXP_CATEGORY_GIFT_INSPIRATION,
        EXP_CATEGORY_LUX_EXCLUSIVE_ID,
        EXP_CATEGORY_LUX_PLUS_ID,
      ]),
    [],
  )

  const travelEssentialsSplitLabels = useMemo(() => ({
    transfers: intl.formatMessage({
      id: 'WGj8M709yLcgGiD9hdij',
      defaultMessage: 'Transfers',
      description: 'Experience search category label for transfer-related travel essentials',
    }),
    loungesAndServices: intl.formatMessage({
      id: 'x8JnpWwJHaV5vW0mX-B0',
      defaultMessage: 'Lounges and Services',
      description: 'Experience search category label for lounge and airport service travel essentials',
    }),
  }), [intl])

  const categoryItems = useMemo((): Array<CategoryItem> => {
    const categoriesWithoutAnything = categories.filter(
      (cat) => !EXCLUDED_CATEGORY_IDS.has(cat.id),
    )
    const childCategoriesByParent = groupBy(
      categoriesWithoutAnything,
      (cat) => cat.parentId,
    )
    const facetsById = arrayToObject(
      categoriesFacets,
      (facet) => facet.categoryId,
    )
    const topLevelCategories = categoriesWithoutAnything.filter(
      (cat) => cat.level === 'parent',
    )
    const selectedCategories = searchFilters.categoryCodes ?? []
    const categoryGroups = primaryCategories.length > 0
      ? primaryCategories
          .filter((categoryGroup) => !EXCLUDED_CATEGORY_IDS.has(categoryGroup.parent.id))
          .map((categoryGroup) => ({
            ...categoryGroup,
            children: categoryGroup.children.filter(
              (category) => !EXCLUDED_CATEGORY_IDS.has(category.id),
            ),
          }))
      : topLevelCategories.map((category) => ({
          parent: category,
          children: childCategoriesByParent.get(category.id) ?? [],
          count: 0,
          relevanceScore: 0,
        }))

    const eligibleItems = categoryGroups
      .map((categoryGroup) => {
        const category = categoryGroup.parent
        const childCategories = sortBy(
          categoryGroup.children,
          (cat) => facetsById[cat.id]?.count ?? 0,
          'desc',
        )
        const relatedCategories = [category, ...childCategories]
        const total = sum(
          childCategories,
          (cat) => facetsById[cat.id]?.count ?? 0,
        )

        return {
          category,
          categoryId: category.id,
          title: category.name,
          name: category.name,
          count: total,
          relatedCategoryIds: relatedCategories.map((cat) => cat.id),
          relevanceScore: Math.max(
            0,
            ...relatedCategories.map(
              (cat) => facetsById[cat.id]?.relevanceScore ?? 0,
            ),
          ),
          items: childCategories.map((cat): CategoryFilterItem => {
            const count = facetsById[cat.id]?.count ?? 0

            const checked =
              selectedCategories.includes(cat.id) ||
              selectedCategories.includes(cat.parentId ?? -1)

            return {
              name: cat.name,
              count,
              hidden: count === 0 && !checked,
              disabled: count === 0,
              value: cat.id,
              checked,
            }
          }),
        }
      })
      // Keep zero-count categories visible while selected so the customer
      // can still reset them from this bar (selections are never cleared).
      .filter(
        (item) =>
          item.count > 0 || item.items.some((child) => child.checked),
      )
    let displayItems = eligibleItems

    // Search-page A/B only: split the real Travel Essentials taxonomy parent
    // into virtual display categories. Do not use these virtual IDs elsewhere.
    if (discoveryRevampEnabled) {
      displayItems = splitTravelEssentialsFilterCategories(eligibleItems, travelEssentialsSplitLabels)
    }

    return sortCategoriesByRelevance(
      place,
      displayItems,
      undefined,
      {
        selectedCategoryIds: discoveryRevampEnabled ? selectedCategories : [],
        useRelevanceRanking: discoveryRevampEnabled,
      },
    )
  }, [
    place,
    discoveryRevampEnabled,
    categories,
    EXCLUDED_CATEGORY_IDS,
    categoriesFacets,
    primaryCategories,
    searchFilters.categoryCodes,
    travelEssentialsSplitLabels,
  ])

  const onResetCategory = useCallback(
    (item: CategoryItem) => {
      const selectedCategories = searchFilters.categoryCodes ?? []
      const nextCategories = getNextCategoryCodesForSearchCategory(
        selectedCategories,
        item.category.id,
        item.items.map((filterItem) => filterItem.value),
        [],
      )
      setSearchFilters({
        ...searchFilters,
        categoryCodes: nextCategories,
      })
    },
    [searchFilters, setSearchFilters],
  )

  // Landmarks opt-in toggle, rendered with the same CategoryToggleButton (icon + selected state)
  // as the category chips so it matches them. No dropdown caret since it is a show/hide toggle.
  // Pinned as the first chip (left of Top Activities) per the 2026-06-17 product decision; it is
  // not part of the category relevance ranking, the category chips reorder behind it.
  const landmarksChip = landmarksToggle?.show ? (
    <CategoryToggleButton
      key="landmarks-toggle"
      kind="secondary"
      orientation="horizontal"
      isSelected={landmarksToggle.active}
      title={intl.formatMessage({
        id: 'yrZfjSAAyVgOj4pACh-N',
        defaultMessage: 'Landmarks',
        description: 'Toggle button to show or hide the landmarks surface on the experience search page',
      })}
      startIcon={LANDMARKS_ICON}
      onClick={landmarksToggle.onToggle}
    />
  ) : undefined

  const filterButtons = categoryItems.map((item) => (
    <CategoryFilterButton
      key={item.category.id}
      categoryItem={item}
      onApplyFilters={onApplyFilters}
      onResetCategory={onResetCategory}
    />
  ))

  const rowChildren = landmarksChip ? [landmarksChip, ...filterButtons] : filterButtons

  useEffect(() => {
    if (isMapView) {
      return
    }

    const row = filtersRowRef.current
    if (!row) {
      return
    }

    const updateOverflow = () => {
      setHasListViewOverflow(row.scrollWidth > row.clientWidth + 1)
    }

    updateOverflow()

    const resizeObserver =
      typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(updateOverflow)
    resizeObserver?.observe(row)
    window.addEventListener('resize', updateOverflow)

    return () => {
      resizeObserver?.disconnect()
      window.removeEventListener('resize', updateOverflow)
    }
  }, [categoryItems.length, isMapView])

  if (categoryItems.length === 0 && !landmarksChip) {
    return null
  }

  return (
    <FiltersContainer>
      {isMapView && (
        <FullWidthFiltersRow>
          {rowChildren}
        </FullWidthFiltersRow>
      )}
      {!isMapView && (
        <ListViewFiltersRow
          ref={filtersRowRef}
          variant="extraLarge"
          $hasOverflow={hasListViewOverflow}
          className={clsx({ 'align-start': Boolean(landmarksChip) })}
        >
          {rowChildren}
        </ListViewFiltersRow>
      )}
    </FiltersContainer>
  )
}

function mapStateToProps(state: App.State) {
  return {
    categories: state.experience.experienceCategories,
  }
}

export default connect(mapStateToProps)(ExperienceSearchCategoryFilters)
